import { Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { Button, IconButton, InformationModal, ItemImageArea } from 'utoniq-ui'

import {
  ConsoleMissionBoxFragment,
  MissionLabel,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useMessage, useRouter, useTranslation } from '@/core'

type ConsoleMissionBoxProps = {
  mission: ConsoleMissionBoxFragment
  onDelete: (data: ConsoleMissionBoxFragment) => Promise<void>
  deletingMission?: boolean
}

export const ConsoleMissionBox: FC<ConsoleMissionBoxProps> = ({
  mission,
  onDelete,
  deletingMission,
}) => {
  const [openModalDelete, setOpenModalDelete] = useState<boolean>(false)
  const { showError, showMessage } = useMessage()
  const { t } = useTranslation('consoleMissions')
  const { generatePath, routerPages } = useRouter()
  const nowTime = new Date().getTime()

  const checkInProgress = () => {
    if (mission.startedAt <= nowTime && nowTime <= mission.expiredAt) {
      return 'inProgress'
    } else {
      return mission.label
    }
  }
  const stateLabelRender = useMemo(() => {
    switch (checkInProgress()) {
      case MissionLabel.New:
        return { bgColor: 'secondary.light', color: 'text.secondary' }
      case 'inProgress':
        return { bgColor: 'primary.dark', color: 'white' }
      case MissionLabel.Expired:
        return { bgColor: 'grey.100', color: 'text.disabled' }
      default:
        return { bgColor: 'secondary.light', color: 'text.secondary' }
    }
  }, [mission.label])

  return (
    <>
      <Card>
        <CardContent sx={{ p: 5 }}>
          <Stack
            sx={{ pb: 4 }}
            direction="row"
            justifyContent="space-between"
            spacing={4}
          >
            <Typography variant="h5" sx={{ ...styleSets.lineCamp(2) }}>
              {mission.title}
            </Typography>
            <Stack direction="row" spacing={3}>
              <IconButton.Pencil
                href={generatePath(routerPages.ConsoleMissionEdit, {
                  missionId: mission.id,
                })}
                size="small"
                color="grey.700"
                bgcolor="grey.100"
              />
              <IconButton.Trash
                onClick={() => setOpenModalDelete(true)}
                size="small"
                color="grey.700"
                bgcolor="grey.100"
              />
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={4}>
            <Stack spacing={2}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ...styleSets.lineCamp(3) }}
              >
                {mission.description}
              </Typography>

              <Stack direction="row" spacing={2}>
                <Chip
                  sx={{
                    bgcolor: stateLabelRender.bgColor,
                    height: 16,
                    '.MuiChip-label': {
                      px: 1,
                    },
                  }}
                  label={
                    <Typography
                      sx={{ display: 'flex' }}
                      color={stateLabelRender.color}
                      variant="overline"
                    >
                      {t(`${checkInProgress()}`)}
                    </Typography>
                  }
                />
                <Chip
                  sx={{
                    bgcolor: 'grey.100',
                    height: 16,
                    '.MuiChip-label': {
                      px: 1,
                    },
                  }}
                  label={
                    <Typography
                      sx={{ display: 'flex' }}
                      color="text.secondary"
                      variant="overline"
                    >
                      {mission.type}
                    </Typography>
                  }
                />
              </Stack>
            </Stack>
            <Stack sx={{ minWidth: 48 }}>
              <ItemImageArea
                size="x-small"
                src={
                  mission?.reward?.item?.item?.thumbnail.front?.smallUri ||
                  mission?.reward?.gachaTicket?.gachaTicket.thumbnails?.reward
                    ?.smallUri ||
                  ''
                }
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <InformationModal
        open={openModalDelete}
        onClose={() => setOpenModalDelete(false)}
        title={t('deleteTitle')}
        content={t('deleteContent')}
        buttons={
          <>
            <Button
              sx={{ minWidth: 212 }}
              variant="contained"
              color="primary"
              size="large"
              loading={deletingMission}
              disabled={deletingMission}
              onClick={async () => {
                await onDelete(mission)
                  .then(() => {
                    showMessage(t('deleteMissionComplete'), 'success')
                    setOpenModalDelete(false)
                  })
                  .catch(showError)
              }}
            >
              {t('okDelete')}
            </Button>
            <Button
              sx={{ minWidth: 212 }}
              variant="text"
              onClick={() => {
                setOpenModalDelete(false)
              }}
            >
              {t('cancelDelete')}
            </Button>
          </>
        }
        sx={{
          // 単に props で指定するだけだと
          // default のスタイルが優先されるため
          // セレクタを用いた CSS 指定を行っている
          '& .MuiBackdrop-root': {
            // 画面幅に依らずバックドロップを画面全体にかける
            maxWidth: '100%',
          },
        }}
      />
    </>
  )
}
